import React, { useEffect, useState } from "react";
import api, { downloadContractSheet, downloadSenioritySheet, downloadEmployeesCsv } from "../../api";
import { toast } from "sonner";
import { UserPlus, Search, MailPlus, TrendingUp, Car, Download, MapPin, AlertTriangle, Sprout, UserMinus, FileSpreadsheet } from "lucide-react";
import { DueDiligenceDialog } from "../../components/dd/DueDiligenceDialog";
import { ddPayload } from "./DdEmployeeFields";
import { useAuth } from "../../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { EMPTY_EMP, contractBadge, scattoInfo } from "./adminUtils";
import { EmployeeRow } from "./EmployeeRow";
import { EmployeeFormDialog } from "./EmployeeFormDialog";
import { EsgBulkDataDialog } from "./EsgBulkDataDialog";
import { ContractAlert } from "./ContractAlert";
import { ConfirmDialog } from "./ConfirmDialog";

const PLAN_NAMES = { starter: "Starter", business: "Business", enterprise: "Enterprise" };

const PlanUsageBar = ({ usage }) => {
  const { count = 0, limit, plan, plan_status } = usage;
  const active = plan_status === "active";
  const unlimited = limit == null;
  const pct = unlimited ? 0 : Math.min(100, Math.round((count / Math.max(1, limit)) * 100));
  const full = !unlimited && count >= limit;
  const near = !unlimited && !full && pct >= 80;
  const barColor = full ? "bg-[#c00510]" : near ? "bg-amber-500" : "bg-[#009640]";
  const planLabel = active && plan ? `Piano ${PLAN_NAMES[plan] || plan}` : "Piano di prova";
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4" data-testid="plan-usage-bar">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
        <span className="text-[13px] font-bold text-[#16345c]">{planLabel}</span>
        <span className="text-[13px] text-gray-600" data-testid="plan-usage-count">
          {unlimited ? <><b className="text-[#16345c]">{count}</b> dipendenti · <span className="text-[#009640] font-semibold">Illimitato</span></>
            : <><b className={full ? "text-[#c00510]" : "text-[#16345c]"}>{count}/{limit}</b> posti usati{full ? "" : ` · ${limit - count} disponibili`}</>}
        </span>
      </div>
      {!unlimited && (
        <div className="h-2.5 w-full bg-gray-100 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} data-testid="plan-usage-fill" />
        </div>
      )}
      {full && (
        <p className="text-[12px] text-[#c00510] font-semibold mt-2" data-testid="plan-usage-full-msg">
          Limite raggiunto: aggiorna il piano di abbonamento per aggiungere altri dipendenti.
        </p>
      )}
    </div>
  );
};

export const EmployeesTab = ({ externalOnly = false, companyFilter = "all" }) => {
  const [list, setList] = useState([]);
  const [depts, setDepts] = useState([]);
  const [ccnls, setCcnls] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [search, setSearch] = useState("");
  const [onlyCars, setOnlyCars] = useState(false);
  const [showTerminated, setShowTerminated] = useState(false);
  const [sedeFilter, setSedeFilter] = useState("all");
  const [form, setForm] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [toWelcome, setToWelcome] = useState(null);
  const [showBulk, setShowBulk] = useState(false);
  const [showEsgBulk, setShowEsgBulk] = useState(false);
  const [saving, setSaving] = useState(false);
  const [usage, setUsage] = useState(null);
  const [showDd, setShowDd] = useState(false);
  const { user } = useAuth();

  const load = () => {
    api.get("/employees").then((r) => setList(r.data)).catch(() => {});
    api.get("/departments").then((r) => setDepts(r.data)).catch(() => {});
    api.get("/ccnl").then((r) => setCcnls(r.data)).catch(() => {});
    api.get("/benefits").then((r) => setBenefits(r.data)).catch(() => {});
    api.get("/company/usage").then((r) => setUsage(r.data)).catch(() => setUsage(null));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const openNew = () => setForm({ ...EMPTY_EMP, is_external: externalOnly, company: companyFilter === "all" ? "teleborsa" : companyFilter });
  const openEdit = (e) => setForm({ ...e, password: "", department_id: e.department_id || "", contract_end: e.contract_end || "", _edit: true });

  const save = async () => {
    if (!form.name || !form.matricola || !form.email || (!form._edit && !form.password)) {
      toast.error("Compila tutti i campi obbligatori"); return;
    }
    setSaving(true);
    try {
      const body = { ...form };
      body.department_id = body.department_id || null;
      body.gender = body.gender || null;
      body.contract_end = body.contract_end || null;
      body.ccnl_id = body.ccnl_id || "";
      body.hire_date = body.hire_date || null;
      body.part_time_pct = Number(body.part_time_pct) || 100;
      body.country = (body.country || "IT");
      body.weekly_hours_contract = (body.weekly_hours_contract === "" || body.weekly_hours_contract == null) ? null : Number(body.weekly_hours_contract);
      body.annual_gross = (body.annual_gross === "" || body.annual_gross == null) ? null : Number(body.annual_gross);
      body.exfestivita_balance = Number(body.exfestivita_balance) || 0;
      body.studio_balance = Number(body.studio_balance) || 0;
      body.prev_ferie_balance = Number(body.prev_ferie_balance) || 0;
      body.prev_rol_balance = Number(body.prev_rol_balance) || 0;
      body.prev_exfestivita_balance = Number(body.prev_exfestivita_balance) || 0;
      Object.assign(body, ddPayload(body));
      if (form._edit) {
        delete body._edit; delete body.id; delete body.matricola;
        if (!body.password) delete body.password;
        await api.put(`/employees/${form.id}`, body);
        toast.success("Dipendente aggiornato");
      } else {
        await api.post("/employees", { ...body, ferie_balance: Number(body.ferie_balance), rol_balance: Number(body.rol_balance) });
        toast.success("Dipendente aggiunto");
      }
      setForm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/employees/${toDelete.id}`); toast.success("Dipendente rimosso"); setToDelete(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const sendWelcome = async () => {
    setSaving(true);
    try {
      await api.post(`/employees/${toWelcome.id}/welcome`);
      toast.success(`Email di benvenuto inviata a ${toWelcome.email}`);
      setToWelcome(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nell'invio dell'email"); }
    finally { setSaving(false); }
  };

  const ccnlMap = React.useMemo(() => Object.fromEntries(ccnls.map((c) => [c.id, c.name])), [ccnls]);
  const ccnlFullMap = React.useMemo(() => Object.fromEntries(ccnls.map((c) => [c.id, c])), [ccnls]);

  const downloadContract = async (e) => {
    try { await downloadContractSheet(e.id, `scheda_contratto_${e.matricola}.pdf`); toast.success("Scheda contratto scaricata"); }
    catch { toast.error("Errore durante il download"); }
  };

  const downloadSeniority = async (e) => {
    try { await downloadSenioritySheet(e.id, `anzianita_${e.matricola}.pdf`); toast.success("Riepilogo anzianità scaricato"); }
    catch { toast.error("Errore durante il download"); }
  };

  const exportCsv = async () => {
    try {
      await downloadEmployeesCsv(companyFilter, externalOnly ? "external" : "internal", externalOnly ? "elenco_collaboratori.csv" : "elenco_dipendenti.csv");
      toast.success("Elenco esportato in CSV");
    } catch { toast.error("Errore durante l'esportazione"); }
  };

  const carByEmployee = React.useMemo(() => {
    const map = {};
    (benefits || []).forEach((b) => { if (b.car_employee_id && (b.car_model || b.car_plate)) map[b.car_employee_id] = b; });
    return map;
  }, [benefits]);

  const scoped = externalOnly ? list.filter((e) => e.is_external) : list;
  const base = companyFilter === "all" ? scoped : scoped.filter((e) => (e.company || "teleborsa") === companyFilter);
  const filtered = base.filter((e) => (e.name + e.matricola + e.email + e.dept).toLowerCase().includes(search.toLowerCase()) && (!onlyCars || carByEmployee[e.id]) && (sedeFilter === "all" || (e.sede || "Roma") === sedeFilter) && (showTerminated || e.active !== false));
  const terminatedCount = base.filter((e) => e.active === false).length;
  const expiringList = base.filter((e) => contractBadge(e.contract_end));
  const pendingOnboarding = base.filter((e) => !e.welcome_sent_at);
  // Dipendenti interni attivi senza CCNL o livello: l'attestato di servizio uscirebbe con "—".
  const missingContractData = externalOnly ? [] : base.filter((e) => e.active !== false && (!e.ccnl_id || !e.level));
  const scattoAlerts = base
    .map((e) => ({ e, s: scattoInfo(e.hire_date, ccnlFullMap[e.ccnl_id]) }))
    .filter((x) => x.s && (x.s.reachedNow || x.s.upcoming));

  const sendBulkWelcome = async () => {
    setSaving(true);
    try {
      const res = await api.post("/employees/welcome-bulk", { ids: pendingOnboarding.map((e) => e.id) });
      toast.success(`Email di benvenuto inviate: ${res.data?.sent ?? 0}`);
      setShowBulk(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nell'invio massivo"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      {!externalOnly && usage?.is_tenant && <PlanUsageBar usage={usage} />}
      <ContractAlert employees={expiringList} />

      {missingContractData.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-3" data-testid="missing-contract-data-alert">
          <span className="text-amber-600 mt-0.5"><AlertTriangle size={18} /></span>
          <div className="text-[13px] text-gray-700">
            <b className="text-amber-800">Dati contrattuali mancanti</b> — {missingContractData.length} {missingContractData.length === 1 ? "dipendente non ha" : "dipendenti non hanno"} CCNL o livello impostato: nell'attestato di servizio questi campi usciranno con "—". Completa l'anagrafica:
            <span className="ml-1">
              {missingContractData.map((e, i) => (
                <span key={e.id}>
                  {i > 0 && ", "}
                  <button onClick={() => openEdit(e)} data-testid={`missing-data-fix-${e.id}`} className="font-bold text-[#16345c] underline underline-offset-2 hover:text-[#0d6cb8] transition-colors">{e.name}</button>
                  <span className="text-gray-500"> ({!e.ccnl_id && !e.level ? "CCNL + livello" : !e.ccnl_id ? "CCNL" : "livello"})</span>
                </span>
              ))}
            </span>
          </div>
        </div>
      )}

      {scattoAlerts.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4 flex items-start gap-3" data-testid="scatto-alert">
          <span className="text-emerald-600 mt-0.5"><TrendingUp size={18} /></span>
          <div className="text-[13px] text-gray-700">
            <b className="text-emerald-800">Scatti di anzianità</b> — {scattoAlerts.length} {scattoAlerts.length === 1 ? "dipendente ha raggiunto o sta per raggiungere" : "dipendenti hanno raggiunto o stanno per raggiungere"} uno scatto:
            <span className="ml-1">
              {scattoAlerts.map((x, i) => (
                <span key={x.e.id}>
                  {i > 0 && ", "}
                  <b className="text-[#16345c]">{x.e.name}</b> ({x.s.reachedNow ? `${x.s.matured}° scatto maturato` : `${x.s.matured + 1}° scatto tra ${x.s.monthsToNext} ${x.s.monthsToNext === 1 ? "mese" : "mesi"}`})
                </span>
              ))}
            </span>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center border border-gray-300 rounded-sm px-3 bg-white w-full sm:w-[280px]">
          <Search size={16} className="text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={externalOnly ? "Cerca collaboratore..." : "Cerca dipendente..."} className="flex-1 py-2 px-2 text-[14px] outline-none" />
        </div>
        <div className="flex items-center border border-gray-300 rounded-sm px-3 bg-white">
          <MapPin size={16} className="text-gray-400" />
          <select value={sedeFilter} onChange={(e) => setSedeFilter(e.target.value)} data-testid="filter-sede" className="py-2 px-1 text-[14px] outline-none bg-white cursor-pointer max-w-[180px]">
            <option value="all">Tutte le sedi</option>
            {[...new Set(base.map((e) => e.sede || "Roma"))].sort().map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        {!externalOnly && (
          <button onClick={() => setOnlyCars((v) => !v)} data-testid="filter-cars-btn" className={`flex items-center gap-2 text-[13px] font-bold px-3 py-2 rounded-sm border transition-colors ${onlyCars ? "bg-[#0d6cb8] text-white border-[#0d6cb8]" : "bg-white text-[#0d6cb8] border-[#0d6cb8] hover:bg-blue-50"}`}><Car size={15} /> Con auto</button>
        )}
        {!externalOnly && terminatedCount > 0 && (
          <button onClick={() => setShowTerminated((v) => !v)} data-testid="filter-terminated-btn" className={`flex items-center gap-2 text-[13px] font-bold px-3 py-2 rounded-sm border transition-colors ${showTerminated ? "bg-gray-600 text-white border-gray-600" : "bg-white text-gray-600 border-gray-400 hover:bg-gray-50"}`}><UserMinus size={15} /> {showTerminated ? "Nascondi cessati" : `Mostra cessati (${terminatedCount})`}</button>
        )}
        <div className="flex items-center gap-2">
          {!externalOnly && user?.role === "admin" && (
            <button onClick={() => setShowDd(true)} data-testid="dd-export-btn" className="flex items-center gap-2 bg-white text-[#16345c] border border-[#16345c] hover:bg-[#16345c] hover:text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors"><FileSpreadsheet size={16} /> Esporta per Due Diligence</button>
          )}
          <button onClick={exportCsv} data-testid="export-employees-csv-btn" className="flex items-center gap-2 bg-white text-[#16345c] border border-[#16345c] hover:bg-[#16345c] hover:text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors"><Download size={16} /> Esporta CSV</button>
          {!externalOnly && (
            <button onClick={() => setShowEsgBulk(true)} data-testid="esg-bulk-btn" className="flex items-center gap-2 bg-white text-[#009640] border border-[#009640] hover:bg-[#009640] hover:text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors"><Sprout size={16} /> Completa dati ESG</button>
          )}
          {!externalOnly && (
            <button onClick={() => setShowBulk(true)} data-testid="bulk-welcome-btn" className="flex items-center gap-2 bg-[#009640] hover:bg-[#007a33] text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors"><MailPlus size={16} /> Onboarding nuovi assunti{pendingOnboarding.length > 0 ? ` (${pendingOnboarding.length})` : ""}</button>
          )}
          <button onClick={openNew} data-testid={externalOnly ? "add-external-btn" : "add-employee-btn"} className="flex items-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors"><UserPlus size={16} /> {externalOnly ? "Aggiungi collaboratore esterno" : "Aggiungi dipendente"}</button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[680px]">
          <thead>
            <tr className="bg-[#16345c] text-white text-[12px] uppercase tracking-wide">
              <th className="text-left px-4 py-3 font-bold">Dipendente</th>
              <th className="text-left px-4 py-3 font-bold">Reparto</th>
              <th className="text-left px-4 py-3 font-bold">Ruolo</th>
              <th className="text-left px-4 py-3 font-bold">CCNL / Part-time</th>
              <th className="text-left px-4 py-3 font-bold">Anzianità</th>
              <th className="text-right px-4 py-3 font-bold">Ferie</th>
              <th className="text-left px-4 py-3 font-bold">Contratto</th>
              <th className="text-right px-4 py-3 font-bold">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => <EmployeeRow key={e.id} e={e} onEdit={openEdit} onDelete={setToDelete} onContract={downloadContract} onSeniority={downloadSeniority} onWelcome={setToWelcome} ccnlName={ccnlMap[e.ccnl_id]} ccnl={ccnlFullMap[e.ccnl_id]} car={carByEmployee[e.id]} />)}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400 text-[13px]">{externalOnly ? "Nessun collaboratore esterno registrato" : "Nessun dipendente trovato"}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <EmployeeFormDialog form={form} setForm={setForm} depts={depts} ccnls={ccnls} benefits={benefits} saving={saving} onSave={save} onClose={() => setForm(null)} />

      <DueDiligenceDialog open={showDd} onClose={() => setShowDd(false)} client={api} basePath="/dd" keysPath="/dd/api-keys" apiCompany={user?.company || "teleborsa"} />

      <EsgBulkDataDialog open={showEsgBulk} onClose={() => setShowEsgBulk(false)} employees={base.filter((e) => e.active !== false)} ccnls={ccnls} onSaved={load} />

      <ConfirmDialog open={!!toDelete} onCancel={() => setToDelete(null)} onConfirm={remove} title="Rimuovere il dipendente?">
        Stai per rimuovere <b>{toDelete?.name}</b> ({toDelete?.matricola}). Verranno eliminati anche cedolini e richieste associate. Operazione non reversibile.
      </ConfirmDialog>

      <ConfirmDialog open={!!toWelcome} onCancel={() => setToWelcome(null)} onConfirm={sendWelcome} confirmLabel="Invia email" confirmClass="bg-[#009640] hover:bg-[#007a33]" title="Inviare l'email di benvenuto?">
        Verrà generata una <b>nuova password temporanea</b> per <b>{toWelcome?.name}</b> e inviata via email a <b>{toWelcome?.email}</b>. Al primo accesso il dipendente dovrà cambiarla obbligatoriamente. La password attuale verrà sostituita.
      </ConfirmDialog>

      <Dialog open={showBulk} onOpenChange={(o) => !o && setShowBulk(false)}>
        <DialogContent className="max-w-[520px] max-h-[85vh] overflow-y-auto" data-testid="bulk-welcome-dialog">
          <DialogHeader>
            <DialogTitle>Onboarding nuovi assunti</DialogTitle>
            <DialogDescription className="text-[13px] text-gray-600">
              Verrà inviata l'email di benvenuto (con password temporanea e cambio password obbligatorio) ai dipendenti che non l'hanno mai ricevuta.
            </DialogDescription>
          </DialogHeader>
          {pendingOnboarding.length === 0 ? (
            <p className="text-[13px] text-gray-500 py-4">Nessun dipendente da onboardare: tutti hanno già ricevuto l'email di benvenuto.</p>
          ) : (
            <div className="border border-gray-200 rounded-sm divide-y divide-gray-100 max-h-[320px] overflow-y-auto">
              {pendingOnboarding.map((e) => (
                <div key={e.id} className="flex items-center justify-between px-3 py-2 text-[13px]">
                  <span className="font-semibold text-[#16345c]">{e.name}</span>
                  <span className="text-gray-500 text-[12px]">{e.matricola} · {e.email}</span>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setShowBulk(false)} className="px-4 py-2 text-[13px] font-semibold text-gray-600 hover:bg-gray-100 rounded-sm transition-colors">Annulla</button>
            <button onClick={sendBulkWelcome} disabled={saving || pendingOnboarding.length === 0} data-testid="bulk-welcome-confirm" className="px-4 py-2 text-[13px] font-bold text-white bg-[#009640] hover:bg-[#007a33] rounded-sm transition-colors disabled:opacity-50">{saving ? "Invio..." : `Invia a ${pendingOnboarding.length}`}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
